import * as React from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

interface LoadingStateProps extends Omit<React.ComponentProps<'div'>, 'title'> {
  title?: React.ReactNode
  description?: React.ReactNode
  variant?: 'block' | 'inline'
}

function LoadingState({
  title = 'Loading…',
  description,
  variant = 'block',
  className,
  ...props
}: LoadingStateProps) {
  if (variant === 'inline') {
    return (
      <div
        data-slot="loading-state"
        data-variant="inline"
        role="status"
        aria-live="polite"
        className={cn(
          'flex items-center gap-2 text-sm text-muted-foreground',
          className,
        )}
        {...props}
      >
        <Loader2 aria-hidden className="size-4 shrink-0 animate-spin" />
        <span>{title}</span>
        {description ? (
          <span className="text-muted-foreground/80">— {description}</span>
        ) : null}
      </div>
    )
  }

  return (
    <div
      data-slot="loading-state"
      data-variant="block"
      role="status"
      aria-live="polite"
      className={cn(
        'flex flex-col items-center gap-3 px-6 py-10 text-center text-muted-foreground',
        className,
      )}
      {...props}
    >
      <Loader2 aria-hidden className="size-5 animate-spin text-primary" />
      <div className="max-w-sm space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description ? (
          <p className="text-sm leading-relaxed">{description}</p>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Render a stable list of skeleton rows so a list-shaped surface doesn't
 * jump when data resolves. Defaults to 3 rows of `h-12` blocks.
 */
interface SkeletonListProps extends React.ComponentProps<'div'> {
  rows?: number
  rowClassName?: string
}

function SkeletonList({
  rows = 3,
  rowClassName,
  className,
  ...props
}: SkeletonListProps) {
  return (
    <div
      data-slot="skeleton-list"
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    >
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className={cn('h-12 w-full', rowClassName)} />
      ))}
    </div>
  )
}

/**
 * Compact inline error callout. Use in place of raw
 * `<p className="text-sm text-destructive">` for errors that appear inside a
 * content surface (e.g. a tab panel or a card section). For page-level errors
 * prefer the route `ErrorFallback`.
 */
interface InlineErrorProps extends Omit<React.ComponentProps<'div'>, 'title'> {
  title?: string
  message?: React.ReactNode
}

function InlineError({
  title = 'Something went wrong',
  message,
  className,
  ...props
}: InlineErrorProps) {
  return (
    <div
      data-slot="inline-error"
      role="alert"
      className={cn(
        'flex items-start gap-2.5 rounded-md border border-destructive/30 bg-destructive-soft/50 p-3',
        className,
      )}
      {...props}
    >
      <AlertTriangle
        aria-hidden
        className="mt-0.5 size-4 shrink-0 text-destructive"
      />
      <div className="min-w-0 flex-1 space-y-0.5 text-sm">
        <p className="font-medium text-destructive-soft-foreground">{title}</p>
        {message ? (
          <p className="leading-relaxed text-destructive-soft-foreground/80">
            {message}
          </p>
        ) : null}
      </div>
    </div>
  )
}

export { LoadingState, SkeletonList, InlineError }
